import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import type { AuditLog } from "@/types";

export interface AuditFilters {
  search: string;
  action: string;
  entity: string;
  branchId: string;
}

export interface AuditStats {
  total: number;
  today: number;
  users: number;
  actions: number;
}

export const EMPTY_AUDIT_FILTERS: AuditFilters = {
  search: "",
  action: "",
  entity: "",
  branchId: "",
};

export function useAuditLogs(filters: AuditFilters, page: number, pageSize = 25) {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<AuditStats>({ total: 0, today: 0, users: 0, actions: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { search, action, entity, branchId } = filters;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = page * pageSize;
      const to = from + pageSize - 1;

      let query = supabase
        .from("audit_logs")
        .select("*", { count: "exact" })
        .order("created_at", { ascending: false });

      const term = search.trim().replace(/,/g, " ");
      if (term) {
        const like = `%${term}%`;
        query = query.or(
          `user_name.ilike.${like},action.ilike.${like},entity.ilike.${like},entity_id.ilike.${like}`,
        );
      }
      if (action) query = query.eq("action", action);
      if (entity) query = query.eq("entity", entity);
      if (branchId) query = query.eq("branch_id", branchId);

      const { data, error: queryError, count } = await query.range(from, to);

      if (queryError) {
        setError("No se pudo cargar el registro de auditoría.");
        setLogs([]);
        setTotal(0);
        return;
      }

      setLogs((data ?? []) as AuditLog[]);
      setTotal(count ?? 0);

      const startToday = new Date();
      startToday.setHours(0, 0, 0, 0);

      const [totalRes, todayRes, recentRes] = await Promise.all([
        supabase.from("audit_logs").select("*", { count: "exact", head: true }),
        supabase
          .from("audit_logs")
          .select("*", { count: "exact", head: true })
          .gte("created_at", startToday.toISOString()),
        supabase
          .from("audit_logs")
          .select("user_name, action")
          .order("created_at", { ascending: false })
          .limit(500),
      ]);

      const recent = (recentRes.data ?? []) as { user_name: string | null; action: string | null }[];
      const users = new Set(recent.map((row) => row.user_name).filter(Boolean)).size;
      const actions = new Set(recent.map((row) => row.action).filter(Boolean)).size;

      setStats({
        total: totalRes.count ?? 0,
        today: todayRes.count ?? 0,
        users,
        actions,
      });
    } catch {
      setError("No se pudo cargar el registro de auditoría.");
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, action, entity, branchId, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  return { logs, total, stats, loading, error, reload: load };
}